import type {
	Count,
	DeploymentFrequencyReportWithProviderInfo,
	Environment,
	LineChartByEnvSeries,
} from '../../../common/types.tsx';
import developmentIcon from './assets/line-chart-development.svg';
import productionIcon from './assets/line-chart-production.svg';
import stagingIcon from './assets/line-chart-staging.svg';
import testingIcon from './assets/line-chart-testing.svg';
import unmappedIcon from './assets/line-chart-unmapped.svg';
import { LINE_COLOR_MAP } from './constants.tsx';

type StartDatesAndCounts = {
	environment: Environment;
	startDateInclusive: Date;
	count: Count;
};

const getEnvironmentSeries = (
	startDatesAndCounts: StartDatesAndCounts[],
	lineColor: string,
	baseLineType: 'solid' | 'dashed',
	icon: string,
	symbolSize: number,
): LineChartByEnvSeries[] => {
	const commonProps = {
		type: 'line',
		color: lineColor,
		symbol: `image://${icon}`,
		symbolSize,
		symbolKeepAspect: false,
	};
	const isCompletelyEmpty = startDatesAndCounts.every(({ count }) => count === 0);

	return [
		// @ts-expect-error - TS2322 - Type '{ data: { value: number | null; customInfo: { startDateInclusive: Date; environment: Environment; }; }[]; lineStyle: { type: "dashed" | "solid"; width: number; }; type: string; color: string; symbol: string; symbolSize: number; symbolKeepAspect: boolean; }' is not assignable to type 'LineChartByEnvSeries'.
		{
			...commonProps,
			data: startDatesAndCounts.map(({ startDateInclusive, count, environment }, i) => {
				const isCurrentWeek = i === startDatesAndCounts.length - 1;
				const value = isCompletelyEmpty || isCurrentWeek ? null : count;

				return {
					value,
					customInfo: { startDateInclusive, environment },
				};
			}),
			lineStyle: {
				type: baseLineType,
				width: 2,
			},
		},
		// @ts-expect-error - TS2322 - Type '{ data: { value: number | null; customInfo: { startDateInclusive: Date; environment: Environment; }; }[]; lineStyle: { type: string; width: number; }; type: string; color: string; symbol: string; symbolSize: number; symbolKeepAspect: boolean; }' is not assignable to type 'LineChartByEnvSeries'.
		{
			...commonProps,
			data: startDatesAndCounts.map(({ startDateInclusive, count, environment }, i) => {
				const isNotLastTwoWeeks = i < startDatesAndCounts.length - 2;
				const value = isCompletelyEmpty || isNotLastTwoWeeks ? null : count;

				return {
					value,
					customInfo: { startDateInclusive, environment },
				};
			}),
			lineStyle: {
				type: 'dashed',
				width: 2,
			},
		},
	];
};

export const convertReportCoreToChartDataPoints = (
	reportData: DeploymentFrequencyReportWithProviderInfo,
): LineChartByEnvSeries[] => [
	...getEnvironmentSeries(
		reportData.weeksData.map((weekData) => ({
			count: weekData.deploymentCount.development,
			startDateInclusive: weekData.startDateInclusive,
			environment: 'development',
		})),
		LINE_COLOR_MAP.development,
		'solid',
		developmentIcon,
		8,
	),
	...getEnvironmentSeries(
		reportData.weeksData.map((weekData) => ({
			count: weekData.deploymentCount.testing,
			startDateInclusive: weekData.startDateInclusive,
			environment: 'testing',
		})),
		LINE_COLOR_MAP.testing,
		'solid',
		testingIcon,
		6,
	),
	...getEnvironmentSeries(
		reportData.weeksData.map((weekData) => ({
			count: weekData.deploymentCount.staging,
			startDateInclusive: weekData.startDateInclusive,
			environment: 'staging',
		})),
		LINE_COLOR_MAP.staging,
		'solid',
		stagingIcon,
		6,
	),
	...getEnvironmentSeries(
		reportData.weeksData.map((weekData) => ({
			count: weekData.deploymentCount.prod,
			startDateInclusive: weekData.startDateInclusive,
			environment: 'production',
		})),
		LINE_COLOR_MAP.prod,
		'solid',
		productionIcon,
		6,
	),
	...getEnvironmentSeries(
		reportData.weeksData.map((weekData) => ({
			count: weekData.deploymentCount.unmapped,
			startDateInclusive: weekData.startDateInclusive,
			environment: 'unmapped',
		})),
		LINE_COLOR_MAP.unmapped,
		'dashed',
		unmappedIcon,
		6,
	),
];
